import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import QuickActionsFAB from './QuickActionsFAB';
import GlobalSearch from './GlobalSearch';
import MobileBanner from './MobileBanner';
import { supabase } from '../lib/supabase';
import ImageDisplay from './ImageDisplay';
import NotificationBell from './NotificationBell';
import KoungaLogo from '../KoungaLogo.png';
import KoungaLogoLight from '../KoungaLogo_Light.png';
import {
  HomeIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  UserGroupIcon,
  CreditCardIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  CalendarDaysIcon,
  CogIcon,
  ShoppingBagIcon,
  UsersIcon,
  ChevronRightIcon,
  UserPlusIcon,
  StarIcon,
  KeyIcon,
  SparklesIcon,
  TrophyIcon,
  PhoneIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  MegaphoneIcon,
  BellIcon,
  ArrowDownTrayIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline';

// Theme-aware logo component
const ThemeAwareLogo = ({ className = "" }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Check if dark mode is active
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };

    // Initial check
    checkDarkMode();

    // Listen for changes to dark mode
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  return (
    <img
      src={isDarkMode ? KoungaLogoLight : KoungaLogo}
      alt="Kounga Logo"
      className={className}
    />
  );
};

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { profile, signOut, isSuperAdmin, isOrganizationOwner, isCenterManager, isCoach, isParent, isStudent, organization, exitImpersonation, isFeatureEnabled } = useAuth();

  // All navigation groups default to collapsed for clean accordion behavior
  const getCollapsedNavigationDefaults = () => {
    return {
      'Management': true,
      'Financial': true,
      'Reports': true,
      'Communication': true,
      'Schedule': true,
      'Training': true,
      'Configuration': true,
      'Special Programs': true,
      'Attendance': true,
      'My Family': true,
      'My Training': true,
      'Academic': true,
      'System': true
    };
  };

  const [collapsedGroups, setCollapsedGroups] = useState(getCollapsedNavigationDefaults);
  const location = useLocation();
  const navigate = useNavigate();

  const fetchUnreadCount = useCallback(async () => {
    if (!profile || !organization) return;

    try {
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organization.id)
        .eq('recipient_id', profile.id)
        .eq('is_read', false);

      if (error) throw error;
      setUnreadCount(count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [profile, organization]);

  // Fetch unread message count
  useEffect(() => {
    if (profile && organization) {
      fetchUnreadCount();

      // Set up real-time subscription for message updates
      const subscription = supabase
        .channel('unread_messages')
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages',
            filter: `recipient_id=eq.${profile.id}`
          },
          () => {
            fetchUnreadCount();
          })
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [profile, organization, fetchUnreadCount]);

  const handleSignOut = async () => {
    try {
      if (!signOut) {
        console.error('signOut function is not available');
        return;
      }
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Navigation items based on user role
  const getNavItems = () => {
    // Super Admin has special navigation
    if (isSuperAdmin()) {
      const superAdminItems = [
        { name: 'Super Admin', href: '/super-admin', icon: HomeIcon },
        { name: 'Manage Users', href: '/super-admin/users', icon: UserGroupIcon },
        { name: 'Financial Management', href: '/super-admin/financial', icon: CurrencyDollarIcon },
        { name: 'System Settings', href: '/super-admin/settings', icon: CogIcon },
        { name: 'Marketing CMS', href: '/super-admin/marketing-cms', icon: MegaphoneIcon },
      ];


      // If impersonating an organization, add organization-specific items
      if (organization) {
        superAdminItems.push(
          { name: '--- Organization View ---', divider: true },
          { name: 'Dashboard', href: '/app/dashboard', icon: HomeIcon },
          { name: 'Centers', href: '/app/centers', icon: BuildingOfficeIcon },
          { name: 'Classes', href: '/app/classes', icon: AcademicCapIcon },
          { name: 'Instructors', href: '/app/coaches', icon: UserIcon },
          { name: 'Managers', href: '/app/managers', icon: UserPlusIcon },
          { name: 'Upcoming Classes', href: '/app/upcoming-classes', icon: CalendarDaysIcon },
          { name: 'Students', href: '/app/students', icon: UserGroupIcon },
          { name: 'Parents', href: '/app/parents', icon: UsersIcon },
          { divider: true, name: 'Finance' },
          { name: 'Monthly Payments', href: '/app/payments', icon: CreditCardIcon },
          { name: 'One-Time Payments', href: '/app/one-time-payments', icon: ShoppingBagIcon },
          { divider: true, name: 'Communication' },
          { name: 'Messages', href: '/app/messages', icon: ChatBubbleLeftRightIcon, badge: unreadCount },
          { divider: true, name: 'Academic' },
          { name: 'Syllabus', href: '/app/syllabus', icon: BookOpenIcon },
          { name: 'Belt Levels', href: '/app/belt-levels', icon: AcademicCapIcon },
          { divider: true, name: 'Attendance' },
          { name: 'Mark Attendance', href: '/app/mark-attendance', icon: CheckCircleIcon },
          { name: 'Attendance Report', href: '/app/attendance-report', icon: ClipboardDocumentListIcon },
          { divider: true, name: 'Special Programs' },
          { name: 'Special Classes', href: '/app/special-classes', icon: SparklesIcon }
        );
      }

      return superAdminItems;
    }

    // Create grouped navigation structure
    const navigationItems = [];

    // Dashboard (always first)
    navigationItems.push({ name: 'Dashboard', href: '/app/dashboard', icon: HomeIcon });

    // Management - reordered as requested
    if (isOrganizationOwner() || isCenterManager()) {
      navigationItems.push({ divider: true, name: 'Management' });
      if (isFeatureEnabled('center_management')) {
        navigationItems.push({ name: 'Centers', href: '/app/centers', icon: BuildingOfficeIcon });
      }

      // Add Managers option only for organization owners (moved to second position)
      if (isOrganizationOwner() && isFeatureEnabled('center_management')) {
        navigationItems.push({ name: 'Managers', href: '/app/managers', icon: UserPlusIcon });
      }

      if (isFeatureEnabled('class_management')) {
        navigationItems.push({ name: 'Classes', href: '/app/classes', icon: AcademicCapIcon });
      }
      if (isFeatureEnabled('coach_management')) {
        navigationItems.push({ name: 'Instructors', href: '/app/coaches', icon: UserIcon });
      }
      if (isFeatureEnabled('parent_management')) {
        navigationItems.push({ name: 'Parents', href: '/app/parents', icon: UsersIcon });
      }
      if (isFeatureEnabled('student_management')) {
        navigationItems.push({ name: 'Students', href: '/app/students', icon: UserGroupIcon });
      }
    } else if (isCoach()) {
      navigationItems.push({ divider: true, name: 'Management' });
      if (isFeatureEnabled('class_management')) {
        navigationItems.push({ name: 'Classes', href: '/app/classes', icon: AcademicCapIcon });
      }
      if (isFeatureEnabled('student_management')) {
        navigationItems.push({ name: 'Students', href: '/app/students', icon: UserGroupIcon });
      }
    } else if (isParent()) {
      navigationItems.push({ divider: true, name: 'My Family' });
      if (isFeatureEnabled('student_management')) {
        navigationItems.push({ name: 'My Children', href: '/app/students', icon: UserGroupIcon });
      }
      if (isFeatureEnabled('class_management')) {
        navigationItems.push({ name: 'My Classes', href: '/app/classes', icon: AcademicCapIcon });
      }
    } else if (isStudent()) {
      // Replace default dashboard with student dashboard for students
      navigationItems[0] = { name: 'My Dashboard', href: '/student-dashboard', icon: HomeIcon };
      navigationItems.push({ divider: true, name: 'My Training' });
      if (isFeatureEnabled('class_management')) {
        navigationItems.push({ name: 'My Classes', href: '/app/classes', icon: AcademicCapIcon });
      }
      if (isFeatureEnabled('student_progress')) {
        navigationItems.push({ name: 'My Progress', href: '/app/student-progress', icon: TrophyIcon });
      }
    }

    // Schedule
    if (isOrganizationOwner() || isCenterManager() || isCoach() || isParent() || isStudent()) {
      if (isFeatureEnabled('class_management')) {
        navigationItems.push({ divider: true, name: 'Schedule' });
        if (isStudent()) {
          navigationItems.push({ name: 'My Schedule', href: '/app/upcoming-classes', icon: CalendarDaysIcon });
        } else {
          navigationItems.push({ name: 'Upcoming Classes', href: '/app/upcoming-classes', icon: CalendarDaysIcon });
        }
      }
    }

    // Special Programs
    if (isOrganizationOwner() || isCenterManager() || isParent()) {
      if (isFeatureEnabled('special_classes')) {
        navigationItems.push({ divider: true, name: 'Special Programs' });
        if (isParent()) {
          navigationItems.push({ name: 'Special Classes', href: '/app/special-classes/parent', icon: SparklesIcon });
        } else {
          navigationItems.push({ name: 'Special Classes', href: '/app/special-classes', icon: SparklesIcon });
        }
      }
    }

    // Tournaments
    if (isOrganizationOwner() || isCenterManager() || isParent() || isStudent()) {
      navigationItems.push({ divider: true, name: 'Tournaments' });
      if (isParent() || isStudent()) {
        navigationItems.push({ name: 'Tournaments', href: '/app/tournament-browse', icon: TrophyIcon });
      } else {
        navigationItems.push({ name: 'Tournament Management', href: '/app/tournaments', icon: TrophyIcon });
        navigationItems.push({ name: 'Event Templates', href: '/app/tournament-events', icon: ClipboardDocumentListIcon });
      }
    }

    // Training & Curriculum
    if (isFeatureEnabled('syllabus_management') || isFeatureEnabled('belt_progression')) {
      navigationItems.push({ divider: true, name: 'Training' });
      if (isFeatureEnabled('syllabus_management')) {
        navigationItems.push({ name: 'Syllabus', href: '/app/syllabus', icon: BookOpenIcon });
      }
      if (isFeatureEnabled('belt_progression')) {
        navigationItems.push({ name: 'Belt Levels', href: '/app/belt-levels', icon: StarIcon });
      }
    }

    // Student Progress - available to all roles
    if ((isOrganizationOwner() || isCenterManager() || isCoach() || isParent() || isStudent()) && isFeatureEnabled('student_progress')) {
      navigationItems.push({ name: 'Student Progress', href: '/app/student-progress', icon: TrophyIcon });
    }

    // Attendance
    if ((isOrganizationOwner() || isCenterManager() || isCoach() || isParent()) && isFeatureEnabled('attendance_system')) {
      navigationItems.push({ divider: true, name: 'Attendance' });

      // Only instructors, managers, and owners can mark attendance
      if (isOrganizationOwner() || isCenterManager() || isCoach()) {
        navigationItems.push({ name: 'Mark Attendance', href: '/app/mark-attendance', icon: CheckCircleIcon });
      }

      // All roles (including parents) can view attendance reports
      navigationItems.push({ name: 'Attendance Report', href: '/app/attendance-report', icon: ClipboardDocumentListIcon });
    }

    // Communication
    if (isFeatureEnabled('internal_messaging')) {
      navigationItems.push({ divider: true, name: 'Communication' });
      navigationItems.push({ name: 'Messages', href: '/app/messages', icon: ChatBubbleLeftRightIcon, badge: unreadCount });
    }

    // Financial
    if (isOrganizationOwner() || isCenterManager() || isParent()) {
      if (isFeatureEnabled('monthly_payments') || isFeatureEnabled('one_time_payments')) {
        navigationItems.push({ divider: true, name: 'Financial' });
        if (isParent()) {
          if (isFeatureEnabled('monthly_payments')) {
            navigationItems.push({ name: 'My Bills', href: '/app/payments', icon: CreditCardIcon });
          }
          if (isFeatureEnabled('one_time_payments')) {
            navigationItems.push({ name: 'My One-Time Payments', href: '/app/one-time-payments', icon: ShoppingBagIcon });
          }
        } else {
          if (isFeatureEnabled('monthly_payments')) {
            navigationItems.push({ name: 'Monthly Payments', href: '/app/payments', icon: CreditCardIcon });
          }
          if (isFeatureEnabled('one_time_payments')) {
            navigationItems.push({ name: 'One-Time Payments', href: '/app/one-time-payments', icon: ShoppingBagIcon });
          }
        }
      }
    }

    // Reports
    if (isOrganizationOwner() || isCenterManager()) {
      if (isFeatureEnabled('tournament_selection') || isFeatureEnabled('contact_lists') || isFeatureEnabled('center_statistics') || isFeatureEnabled('reports_analytics')) {
        navigationItems.push({ divider: true, name: 'Reports' });
        if (isFeatureEnabled('reports_analytics')) {
          navigationItems.push({ name: 'Advanced Analytics', href: '/app/advanced-reports', icon: PresentationChartLineIcon });
        }
        if (isFeatureEnabled('tournament_selection')) {
          navigationItems.push({ name: 'Tournament Selection', href: '/app/tournament-selection', icon: TrophyIcon });
        }
        if (isFeatureEnabled('contact_lists')) {
          navigationItems.push({ name: 'Contact List', href: '/app/contact-list', icon: PhoneIcon });
        }
        if (isFeatureEnabled('center_statistics')) {
          navigationItems.push({ name: 'Center Statistics', href: '/app/center-statistics', icon: ChartBarIcon });
        }
      }
    }

    // Configuration (moved to last)
    if (isOrganizationOwner() || isCenterManager()) {
      navigationItems.push({ divider: true, name: 'Configuration' });
      if (isFeatureEnabled('registration_tokens')) {
        navigationItems.push({ name: 'Registration Tokens', href: '/app/token-management', icon: KeyIcon });
      }

      // Add Organization Settings for organization owners only
      if (isOrganizationOwner()) {
        navigationItems.push({ name: 'Organization Settings', href: '/app/organization-settings', icon: CogIcon });
      }
    }

    return navigationItems;
  };

  // Group navigation items for mobile collapsible menu
  const getGroupedNavItems = () => {
    const items = getNavItems();
    const groups = [];
    let currentGroup = null;
    let dashboardItem = null;

    items.forEach(item => {
      if (item.divider) {
        if (currentGroup && currentGroup.items.length > 0) {
          groups.push(currentGroup);
        }
        currentGroup = {
          name: item.name,
          items: []
        };
      } else {
        // Check if this is a Dashboard item (should be standalone)
        if (item.name === 'Dashboard' || item.name === 'My Dashboard') {
          dashboardItem = item;
        } else {
          if (!currentGroup) {
            // Non-dashboard items before first divider should not be grouped
            // This shouldn't happen with our current structure, but adding safety
            currentGroup = {
              name: 'Other',
              items: []
            };
          }
          currentGroup.items.push(item);
        }
      }
    });

    // Don't forget the last group
    if (currentGroup && currentGroup.items.length > 0) {
      groups.push(currentGroup);
    }

    return { dashboardItem, groups };
  };

  // Accordion-style navigation: only one group can be expanded at a time
  const toggleGroup = (groupName) => {
    setCollapsedGroups(prev => {
      const isCurrentlyCollapsed = prev[groupName];

      if (isCurrentlyCollapsed) {
        // Expanding a group: collapse all others and expand this one
        const newState = {};
        Object.keys(prev).forEach(key => {
          newState[key] = key !== groupName; // Collapse all except the clicked one
        });
        return newState;
      } else {
        // Collapsing the currently expanded group
        return {
          ...prev,
          [groupName]: true
        };
      }
    });
  };

  const { dashboardItem, groups } = getGroupedNavItems();

  // Collapsible navigation group component for mobile
  const CollapsibleNavGroup = ({ group, mobile = false }) => {
    const isCollapsed = collapsedGroups[group.name];
    const hasActiveItem = group.items.some(item => location.pathname === item.href);

    return (
      <div className="mb-1">
        <button
          onClick={() => toggleGroup(group.name)}
          className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 touch-manipulation ${hasActiveItem
            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 shadow-sm'
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 active:bg-gray-100 dark:active:bg-gray-600'
            }`}
        >
          <span className="text-xs uppercase tracking-wider font-bold">{group.name}</span>
          <div className={`transition-transform duration-200 ${isCollapsed ? '' : 'rotate-90'}`}>
            <ChevronRightIcon className="h-4 w-4" />
          </div>
        </button>

        <div className={`mt-1 transition-all duration-300 ease-in-out overflow-hidden ${isCollapsed ? 'max-h-0 opacity-0' : mobile ? 'max-h-screen opacity-100' : 'max-h-96 opacity-100'
          }`}>
          <div className="pl-2 space-y-1 py-1">
            {group.items.map((item) => (
              <NavLink key={item.name} item={item} mobile={mobile} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const NavLink = ({ item, mobile = false }) => {

    if (item.divider) {
      return (
        <div className={mobile ? "px-2 py-2" : "px-2 py-2"}>
          <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            {item.name}
          </div>
        </div>
      );
    }

    const isActive = location.pathname === item.href;
    const baseClasses = mobile
      ? 'group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 touch-manipulation'
      : 'group flex items-center px-2 py-2 text-sm font-medium rounded-md';

    const activeClasses = isActive
      ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-900 dark:text-blue-400 shadow-sm border-l-2 border-blue-500 dark:border-blue-400'
      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 active:bg-gray-100 dark:active:bg-gray-600';

    return (
      <Link
        to={item.href}
        className={`${baseClasses} ${activeClasses}`}
        onClick={() => mobile && setSidebarOpen(false)}
      >
        <item.icon
          className={`mr-3 flex-shrink-0 h-5 w-5 ${isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
            }`}
        />
        <span className="flex-1 text-left">{item.name}</span>
        {item.badge && item.badge > 0 && (
          <span className="bg-red-500 text-white text-xs font-medium px-2 py-0.5 rounded-full ml-auto">
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div>
      {/* Mobile Banner for Desktop Recommendation */}
      <MobileBanner />

      {/* Impersonation Banner */}
      {isSuperAdmin() && organization && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-yellow-500 text-black text-center py-2 text-sm font-medium" style={{ marginTop: '0' }}>
          <div className="flex items-center justify-center space-x-4">
            <span>🔍 Impersonating: <strong>{organization.name}</strong></span>
            <button
              onClick={() => {
                exitImpersonation();
                navigate('/super-admin');
              }}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-xs"
            >
              Exit Impersonation
            </button>
          </div>
        </div>
      )}

      <div className={`flex h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-300 ${isSuperAdmin() && organization ? 'pt-10' : 'pt-0'}`}>
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div
              className="fixed inset-0 bg-gray-600 bg-opacity-75"
              onClick={() => setSidebarOpen(false)}
            />
          </div>
        )}

        {/* Mobile sidebar */}
        <div className={`fixed left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out lg:hidden ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } flex flex-col`} style={{ top: isSuperAdmin() && organization ? '80px' : '40px', bottom: '0' }}>
          {/* Fixed header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0 transition-colors duration-300">
            <div className="flex items-center space-x-3">
              <ImageDisplay
                imageUrl={organization?.image_url}
                entityType="organization"
                alt={`${organization?.name || 'Organization'} logo`}
                size="sm"
                className="flex-shrink-0"
              />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">{organization?.name || 'Club'}</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors duration-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Search section in mobile sidebar */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
            <GlobalSearch />
          </div>

          {/* Scrollable navigation */}
          <nav className="flex-1 px-4 py-4 overflow-y-auto min-h-0">
            <div className="space-y-2">
              {/* Dashboard as standalone item */}
              {dashboardItem && (
                <div className="mb-4">
                  <NavLink item={dashboardItem} mobile />
                </div>
              )}

              {/* Grouped navigation items */}
              {groups.map((group) => (
                <CollapsibleNavGroup key={group.name} group={group} mobile />
              ))}
            </div>
          </nav>

          {/* Fixed footer */}
          <div className="bg-white dark:bg-gray-800 flex-shrink-0">
            {/* Profile section separator - not touching edges */}
            <div className="mx-3 border-t border-gray-100 dark:border-gray-700"></div>
            {/* User Profile Section */}
            <div className="p-3 border-b border-gray-100 dark:border-gray-700">
              <Link
                to="/app/profile"
                className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                onClick={() => setSidebarOpen(false)}
              >
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    {profile?.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <UserIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    )}
                  </div>
                </div>
                <div className="ml-2 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                    {profile?.full_name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize truncate">
                    {profile?.role === 'organization_owner' ? '🏢 Owner' :
                      profile?.role === 'center_manager' ? '🏛️ Manager' :
                        profile?.role === 'coach' ? '🥋 Instructor' :
                          profile?.role === 'parent' ? '👨‍👩‍👧‍👦 Parent' :
                            profile?.role === 'student' ? '🧒 Student' :
                              profile?.role?.replace('_', ' ') || 'User'}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            </div>

            {/* Notification Preferences Link */}
            <div className="px-3 pb-2">
              <Link
                to="/app/notification-preferences"
                className="group flex items-center px-2 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors"
                onClick={() => setSidebarOpen(false)}
              >
                <svg className="mr-2 h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                Notifications
              </Link>
            </div>

            {/* Sign Out Button */}
            <div className="px-3 py-2">
              <button
                onClick={handleSignOut}
                className="group flex w-full items-center px-2 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowRightOnRectangleIcon className="mr-2 h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400" />
                Sign out
              </button>
            </div>

            {/* Company Branding */}
            <div className="px-3 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-center transition-colors duration-300">
              {/* Company Logo & Info */}
              <div className="space-y-3">
                {/* Company Logo */}
                <div className="flex justify-center items-center">
                  <ThemeAwareLogo className="w-16 h-16 rounded-lg object-contain" />
                </div>

                {/* Powered by text */}
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Powered by{' '}
                    <a
                      href="https://koungasolutions.co.nz/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
                    >
                      Kounga Solutions
                    </a>
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">v1.0</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop sidebar */}
        <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
          <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 pt-5 pb-4 overflow-y-auto transition-colors duration-300">
            <div className="flex items-center flex-shrink-0 px-4 space-x-3">
              <ImageDisplay
                imageUrl={organization?.image_url}
                entityType="organization"
                alt={`${organization?.name || 'Organization'} logo`}
                size="md"
                className="flex-shrink-0"
              />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">{organization?.name || 'Club'}</h1>
            </div>

            {/* Desktop Search */}
            <div className="mt-4 px-4">
              <GlobalSearch />
            </div>

            <div className="mt-5 flex-grow flex flex-col">
              <nav className="flex-1 px-2 space-y-2">
                {/* Dashboard as standalone item */}
                {dashboardItem && (
                  <div className="mb-4">
                    <NavLink item={dashboardItem} mobile={false} />
                  </div>
                )}

                {/* Grouped navigation items */}
                {groups.map((group) => (
                  <CollapsibleNavGroup key={group.name} group={group} mobile={false} />
                ))}
              </nav>
            </div>

            <div className="flex-shrink-0 p-4">
              {/* Profile section separator - not touching edges */}
              <div className="mb-4 mx-2 border-t border-gray-100 dark:border-gray-700"></div>
              <Link
                to="/app/profile"
                className="flex items-center mb-4 px-2 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
              >
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-600 flex items-center justify-center">
                    {profile?.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <UserIcon className="h-6 w-6 text-gray-400 dark:text-gray-300" />
                    )}
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {profile?.full_name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {profile?.role?.replace('_', ' ')}
                  </p>
                </div>
              </Link>

              {/* Notification Preferences Link */}
              <Link
                to="/app/notification-preferences"
                className="group flex items-center px-2 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-200 transition-colors mb-4"
              >
                <div className="flex-shrink-0 w-10 flex justify-center">
                  <svg className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <span className="ml-3">Notifications</span>
              </Link>

              <button
                onClick={handleSignOut}
                className="group flex w-full items-center px-2 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-200 transition-colors mb-4"
              >
                <div className="flex-shrink-0 w-10 flex justify-center">
                  <ArrowRightOnRectangleIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400" />
                </div>
                <span className="ml-3">Sign out</span>
              </button>

              {/* Company Branding for Desktop */}
              <div className="pt-4 border-t border-gray-100 dark:border-gray-700 text-center transition-colors duration-300">
                <div className="space-y-3">
                  {/* Company Logo */}
                  <div className="flex justify-center items-center">
                    <ThemeAwareLogo className="w-16 h-16 rounded-lg object-contain" />
                  </div>

                  {/* Powered by text */}
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Powered by{' '}
                      <a
                        href="https://koungasolutions.co.nz"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
                      >
                        Kounga Solutions
                      </a>
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      v2.0.0
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-col flex-1 lg:pl-64">
          {/* Mobile header */}
          <div className={`sticky z-10 flex-shrink-0 flex h-16 bg-white dark:bg-gray-800 shadow lg:hidden transition-colors duration-300`} style={{ top: isSuperAdmin() && organization ? '80px' : '40px' }}>
            <button
              onClick={() => setSidebarOpen(true)}
              className="px-4 border-r border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            <div className="flex-1 px-4 flex justify-between items-center">
              <div className="flex-1 flex items-center min-w-0">
                {/* User name for mobile */}
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                  {profile?.first_name || profile?.full_name?.split(' ')[0] || 'User'}
                </p>
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0">
                {/* Notification Bell */}
                <NotificationBell />

                {/* Theme Toggle for Mobile */}
                <ThemeToggle variant="icon" />
              </div>
            </div>
          </div>

          {/* Page content */}
          <main className="flex-1 overflow-y-auto pb-20 lg:pb-0">
            <div className="py-6">
              <Outlet />
            </div>
          </main>

          {/* Footer */}
          <footer className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 transition-colors duration-300">
            <div className="w-full py-4 pl-4 pr-6 sm:pl-6 sm:pr-8 lg:pl-8 lg:pr-12">
              <div className="flex items-center justify-between">
                {/* Left spacer */}
                <div className="flex-1"></div>

                {/* Centered copyright text */}
                <div className="flex-shrink-0">
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                    © 2026 All rights reserved{' '}
                    <a
                      href="https://koungasolutions.co.nz"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                    >
                      Kounga Solutions
                    </a>
                  </p>
                </div>

                {/* Right side with theme toggle - positioned at far right with extra padding */}
                <div className="flex-1 flex justify-end">
                  {/* Theme toggle hidden in mobile view */}
                  <div className="hidden lg:block">
                    <ThemeToggle variant="button" />
                  </div>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>

      {/* Quick Actions Floating Button */}
      <QuickActionsFAB />
    </div>
  );
};

export default Layout;